import * as React from "react";
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import useApi from "@/hooks/useApi";
import LoadingSpinner from "@/components/LoadingSpinner";
import AuthService from "@/services/AuthService";
import type { DashboardData } from "@/hooks/useLiveDashboard";

const messagingClientUrl = import.meta.env.VITE_MESSAGING_CLIENT_URL as string;
const isVelaroInternal = AuthService.profile.siteId === 1032;

interface RuleActivityEntry {
  ruleId?: number;
  ruleType: string;
  priority?: number;
  outcome?: string;
}

interface WorkflowActivityEntry {
  workflowId?: number;
  timestamp: string;
  workflowName: string;
  triggerType: string;
  conversationId?: number | string;
  outcome?: string;
}

interface ScheduledJobEntry {
  id: string;
  cron: string;
  nextExecution?: string;
  lastExecution?: string;
  state?: string;
}

interface ActiveBreakdown {
  inWorkflow: number;
  inRule: number;
  idleMatching: number;
  total: number;
}

interface BotErrorEntry {
  integration?: string;
  message?: string;
  additionalInfo?: string;
  createdAt: string;
  suppressedCount?: number;
}

interface BotSummary {
  totalSkillCalls: number;
  errorCount: number;
  successRate: number;
  topSkill: string;
  topSkillCount: number;
}

function Section({
  title,
  subtitle,
  count,
  children,
  action
}: {
  title: string;
  subtitle?: string;
  count?: number;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white mb-5">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-slate-800">{title}</h2>
          {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-3">
          {action}
          {typeof count === "number" && (
            <span className="text-xs text-slate-500">{count} entries</span>
          )}
        </div>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function KpiCard({
  label,
  value,
  sub,
  warn
}: {
  label: string;
  value: number | string;
  sub?: string;
  warn?: boolean;
}) {
  return (
    <div className="text-center">
      <div className={`text-2xl font-semibold ${warn ? "text-amber-600" : "text-slate-800"}`}>
        {value}
      </div>
      <div className="text-xs text-slate-500 mt-1">{label}</div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function ConvLink({ id }: { id?: number | string }) {
  if (!id) return <>—</>;
  return (
    <a
      href={`${messagingClientUrl}/conversations/${id}`}
      target="velaro-workspace"
      rel="noreferrer"
      className="text-blue-600 hover:underline"
    >
      {id}
    </a>
  );
}

function OutcomeBadge({ value }: { value?: string }) {
  if (!value) return <span className="text-slate-400">—</span>;
  const v = value.toLowerCase();
  const cls =
    v === "enabled" || v === "success" || v === "completed"
      ? "bg-green-50 text-green-700 border border-green-200"
      : v === "disabled"
      ? "bg-slate-100 text-slate-500 border border-slate-200"
      : v === "error" || v === "failed"
      ? "bg-red-50 text-red-700 border border-red-200"
      : "bg-blue-50 text-blue-700 border border-blue-200";
  return (
    <span className={`inline-block text-xs rounded px-1.5 py-0.5 font-medium ${cls}`}>
      {value}
    </span>
  );
}

export default function WhatsRunning() {
  const api = useApi();
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState<RuleActivityEntry[]>([]);
  const [workflows, setWorkflows] = useState<WorkflowActivityEntry[]>([]);
  const [jobs, setJobs] = useState<ScheduledJobEntry[]>([]);
  const [breakdown, setBreakdown] = useState<ActiveBreakdown | null>(null);
  const [liveData, setLiveData] = useState<DashboardData | null>(null);
  const [botErrors, setBotErrors] = useState<BotErrorEntry[]>([]);
  const [botSummary, setBotSummary] = useState<BotSummary | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const load = useCallback(async () => {
    async function safeAdmin<T>(path: string, fallback: T): Promise<T> {
      try {
        const res = await api.admin.get(path);
        if (!res.ok) return fallback;
        const data = await res.json();
        return (data ?? fallback) as T;
      } catch {
        return fallback;
      }
    }

    async function safeMessaging<T>(path: string, fallback: T): Promise<T> {
      try {
        const res = await api.messaging.get(path);
        if (!res.ok) return fallback;
        const data = await res.json();
        return (data ?? fallback) as T;
      } catch {
        return fallback;
      }
    }

    const [snap, errors, summary, r, w, j, b] = await Promise.all([
      safeAdmin<DashboardData | null>("LiveDashboard/snapshot", null),
      safeAdmin<BotErrorEntry[]>("BotAnalytics/RecentErrors?days=7&limit=20", []),
      safeAdmin<BotSummary | null>("BotAnalytics/Summary?days=30", null),
      safeMessaging<RuleActivityEntry[]>("Rules/activity", []),
      safeMessaging<WorkflowActivityEntry[]>("Workflows/activity", []),
      safeMessaging<ScheduledJobEntry[]>("Workflows/scheduled-jobs", []),
      safeMessaging<ActiveBreakdown | null>("Workflows/active-breakdown", null),
    ]);

    setLiveData(snap);
    setBotErrors(Array.isArray(errors) ? errors : []);
    setBotSummary(summary);
    setRules(Array.isArray(r) ? r : []);
    setWorkflows(Array.isArray(w) ? w : []);
    setJobs(Array.isArray(j) ? j : []);
    setBreakdown(b);
    setLastRefresh(new Date());
    setLoading(false);
  }, [api]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30_000);
    return () => clearInterval(interval);
  }, [load]);

  if (loading) return <LoadingSpinner />;

  const kpis = liveData?.kpis;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-5">
        <h1 className="text-xl font-semibold text-slate-800">What's Running</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Live activity across agents, automations, and bots.{" "}
          <Link to="/rules" className="underline">Rules</Link>{" · "}
          <Link to="/workflows" className="underline">Workflows</Link>{" · "}
          <Link to="/chat-preview" className="underline">Preview Chat</Link>
          {lastRefresh && (
            <span className="ml-2 text-slate-400">
              · refreshed {lastRefresh.toLocaleTimeString()}
            </span>
          )}
        </p>
      </div>

      {/* Live agent + conversation KPIs */}
      {kpis && (
        <Section title="Live Overview">
          <div className="grid grid-cols-4 gap-4 text-center">
            <KpiCard label="Agents online" value={kpis.agentsOnline} sub={`${kpis.agentsAvailable} available`} />
            <KpiCard label="Open conversations" value={kpis.openConversations} />
            <KpiCard label="In queue" value={kpis.queuedConversations} warn={kpis.queuedConversations > 0} />
            <KpiCard label="Bot-handled" value={kpis.botActive} />
          </div>

          {liveData?.channelBreakdown && liveData.channelBreakdown.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {liveData.channelBreakdown.map((ch) => (
                <span key={ch.channel} className="inline-flex items-center gap-1.5 text-xs bg-slate-100 text-slate-700 rounded-full px-2.5 py-1">
                  {ch.channel}
                  <span className="font-semibold">{ch.count}</span>
                  {ch.queued > 0 && <span className="text-amber-600 font-semibold">({ch.queued} queued)</span>}
                </span>
              ))}
            </div>
          )}

          {botSummary && botSummary.totalSkillCalls > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500">
              <span>
                <span className="font-medium text-slate-700">{botSummary.totalSkillCalls.toLocaleString()}</span>{" "}skill calls (30d)
              </span>
              <span>
                <span className={`font-medium ${botSummary.successRate < 90 ? "text-amber-600" : "text-slate-700"}`}>{botSummary.successRate}%</span>{" "}success rate
              </span>
              {botSummary.topSkill && botSummary.topSkill !== "—" && (
                <span>Top skill: <span className="font-medium text-slate-700">{botSummary.topSkill}</span> ({botSummary.topSkillCount.toLocaleString()}×)</span>
              )}
            </div>
          )}
        </Section>
      )}

      {/* Conversation state breakdown */}
      {breakdown && (
        <Section title="Conversation State">
          <div className="grid grid-cols-4 gap-4 text-center">
            <KpiCard label="Total active" value={breakdown.total} />
            <KpiCard label="In a workflow" value={breakdown.inWorkflow} />
            <KpiCard label="In a rule" value={breakdown.inRule} />
            <KpiCard label="Idle-matching" value={breakdown.idleMatching} />
          </div>
        </Section>
      )}

      {/* Bot / automation errors */}
      <Section
        title="Bot / skill errors"
        subtitle="Last 7 days — errors only, not full activity log"
        count={botErrors.length > 0 ? botErrors.length : undefined}
      >
        {botErrors.length === 0 ? (
          <p className="text-sm text-green-600 flex items-center gap-1.5">
            <span>✓</span> No errors in the last 7 days
          </p>
        ) : (
          <div className="max-h-64 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white">
                <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                  <th className="py-2 font-medium">Time</th>
                  <th className="py-2 font-medium">Integration</th>
                  <th className="py-2 font-medium">Error</th>
                  <th className="py-2 font-medium">Detail</th>
                </tr>
              </thead>
              <tbody>
                {botErrors.map((e, i) => (
                  <tr key={i} className="border-b border-slate-100 last:border-0">
                    <td className="py-2 text-slate-600 whitespace-nowrap">{new Date(e.createdAt).toLocaleString()}</td>
                    <td className="py-2 text-slate-800">{e.integration ?? "—"}</td>
                    <td className="py-2 text-red-600 max-w-xs truncate">{e.message ?? "—"}</td>
                    <td className="py-2 text-slate-500 max-w-xs truncate text-xs">
                      {e.additionalInfo ?? "—"}
                      {e.suppressedCount != null && e.suppressedCount > 0 && (
                        <span className="ml-1 text-slate-400">(+{e.suppressedCount} suppressed)</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Workflows */}
      <Section
        title="Workflows"
        subtitle="Recently modified — click a name to edit"
        count={workflows.length}
        action={<Link to="/workflows" className="text-xs text-blue-600 underline">All workflows →</Link>}
      >
        {workflows.length === 0 ? (
          <p className="text-sm text-slate-500">No workflows configured.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                <th className="py-2 font-medium">Last modified</th>
                <th className="py-2 font-medium">Workflow</th>
                <th className="py-2 font-medium">Trigger</th>
                <th className="py-2 font-medium">Conversation</th>
                <th className="py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {workflows.map((w, i) => (
                <tr key={i} className="border-b border-slate-100 last:border-0">
                  <td className="py-2 text-slate-600 whitespace-nowrap">{new Date(w.timestamp).toLocaleString()}</td>
                  <td className="py-2 font-medium">
                    {w.workflowId ? (
                      <Link to={`/workflows/${w.workflowId}`} className="text-blue-600 hover:underline">{w.workflowName}</Link>
                    ) : (
                      <Link to="/workflows" className="text-blue-600 hover:underline">{w.workflowName}</Link>
                    )}
                  </td>
                  <td className="py-2 text-slate-600">{w.triggerType}</td>
                  <td className="py-2"><ConvLink id={w.conversationId} /></td>
                  <td className="py-2"><OutcomeBadge value={w.outcome} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {/* Rules */}
      <Section
        title="Rules"
        subtitle="Configured rules for this site — click a rule type to manage"
        count={rules.length}
        action={<Link to="/rules" className="text-xs text-blue-600 underline">All rules →</Link>}
      >
        {rules.length === 0 ? (
          <p className="text-sm text-slate-500">No rules configured.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                <th className="py-2 font-medium">Type</th>
                <th className="py-2 font-medium">Priority</th>
                <th className="py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r, i) => (
                <tr key={i} className="border-b border-slate-100 last:border-0">
                  <td className="py-2 font-medium">
                    <Link to="/rules" className="text-blue-600 hover:underline">{r.ruleType}</Link>
                  </td>
                  <td className="py-2 text-slate-600">{r.priority ?? "—"}</td>
                  <td className="py-2"><OutcomeBadge value={r.outcome} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {/* Scheduled / recurring jobs — internal only */}
      {isVelaroInternal && (
        <Section
          title="Scheduled / recurring jobs"
          subtitle="Internal — Velaro staff only"
          count={jobs.length}
          action={<Link to="/admin/hangfire" className="text-xs text-blue-600 underline">Hangfire monitor →</Link>}
        >
          {jobs.length === 0 ? (
            <p className="text-sm text-slate-500">No scheduled jobs configured.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                  <th className="py-2 font-medium">Job</th>
                  <th className="py-2 font-medium">Cron</th>
                  <th className="py-2 font-medium">Last run</th>
                  <th className="py-2 font-medium">Next run</th>
                  <th className="py-2 font-medium">State</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => (
                  <tr key={j.id} className="border-b border-slate-100 last:border-0">
                    <td className="py-2 text-slate-800">{j.id}</td>
                    <td className="py-2 text-slate-600 font-mono text-xs">{j.cron}</td>
                    <td className="py-2 text-slate-600 whitespace-nowrap">{j.lastExecution ? new Date(j.lastExecution).toLocaleString() : "—"}</td>
                    <td className="py-2 text-slate-600 whitespace-nowrap">{j.nextExecution ? new Date(j.nextExecution).toLocaleString() : "—"}</td>
                    <td className="py-2"><OutcomeBadge value={j.state} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>
      )}
    </div>
  );
}
